import type { PlayerObservationV1 } from "@fate-kingdom/engine";

import { AlphaGameIcon } from "../icons/AlphaGameIcon";
import { getAlphaDisplayName } from "./alpha-card-presentation";
import type { AlphaPresentationTransaction } from "./presentation-transaction";

function tokenCount(count: number): readonly number[] {
  return Array.from({ length: Math.min(10, Math.max(0, count)) }, (_, index) => index);
}

function eventCount(
  transaction: AlphaPresentationTransaction,
  type: "cards-drawn" | "cleanup-hand-discarded",
): number {
  return transaction.events.reduce((count, event) => {
    if (event.type === "cards-drawn" && type === "cards-drawn") {
      return count + event.cardInstanceIds.length;
    }
    if (event.type === "cleanup-hand-discarded" && type === "cleanup-hand-discarded") {
      return count + event.cardInstanceIds.length;
    }
    return count;
  }, 0);
}

function eventPlayerIsPerspective(
  transaction: AlphaPresentationTransaction,
  type: "cards-drawn" | "cleanup-hand-discarded",
): boolean {
  return transaction.events.some(
    (event) =>
      event.type === type && event.playerId === transaction.beforeObservation.perspectivePlayerId,
  );
}

function CardTokens({
  count,
  labels,
  faceDown,
  motion,
}: {
  readonly count: number;
  readonly labels?: readonly string[] | undefined;
  readonly faceDown: boolean;
  readonly motion: "cleanup" | "draw";
}) {
  return (
    <span className={`alpha-presentation-cards is-${motion}`} aria-hidden="true">
      {tokenCount(count).map((index) => (
        <span
          className={`alpha-presentation-card-token ${faceDown ? "is-face-down" : ""}`}
          style={{ "--alpha-presentation-index": index } as React.CSSProperties}
          key={index}
        >
          {faceDown ? (
            <AlphaGameIcon name="deck" aria-hidden="true" />
          ) : (
            <small>{labels?.[index] ?? "Card"}</small>
          )}
        </span>
      ))}
    </span>
  );
}

export function ActionPresentationDirector({
  transaction,
  openingObservation,
  duration,
}: {
  readonly transaction?: AlphaPresentationTransaction | undefined;
  readonly openingObservation?: PlayerObservationV1 | undefined;
  readonly duration: number;
}) {
  if (transaction === undefined && openingObservation === undefined) return null;

  if (transaction === undefined) {
    const labels = openingObservation!.own.hand.map(getAlphaDisplayName);
    return (
      <div
        className="alpha-presentation-director is-opening-draw"
        style={{ "--alpha-presentation-duration": `${String(duration)}ms` } as React.CSSProperties}
        role="status"
        aria-label={`Opening hand drawn, ${String(labels.length)} cards`}
      >
        <b>Opening hand</b>
        <CardTokens count={labels.length} labels={labels} faceDown={false} motion="draw" />
      </div>
    );
  }

  const cleanupCount = eventCount(transaction, "cleanup-hand-discarded");
  const drawCount = eventCount(transaction, "cards-drawn");
  const cleanupIsPerspective = eventPlayerIsPerspective(transaction, "cleanup-hand-discarded");
  const drawIsPerspective = eventPlayerIsPerspective(transaction, "cards-drawn");
  const cleanupLabels = cleanupIsPerspective
    ? transaction.beforeObservation.own.hand.map(getAlphaDisplayName)
    : undefined;
  const drawLabels = drawIsPerspective
    ? transaction.afterObservation.own.hand
        .slice(Math.max(0, transaction.afterObservation.own.hand.length - drawCount))
        .map(getAlphaDisplayName)
    : undefined;
  const recycled = transaction.events.some((event) => event.type === "deck-recycled");

  return (
    <div
      className={`alpha-presentation-director is-${transaction.presentationKind}`}
      style={{ "--alpha-presentation-duration": `${String(duration)}ms` } as React.CSSProperties}
      aria-hidden="true"
    >
      {cleanupCount > 0 ? (
        <span className="alpha-presentation-step is-cleanup">
          <CardTokens
            count={cleanupCount}
            labels={cleanupLabels}
            faceDown={!cleanupIsPerspective}
            motion="cleanup"
          />
          <small>Hand to discard</small>
        </span>
      ) : null}
      {recycled ? (
        <span className="alpha-presentation-recycle">
          <AlphaGameIcon name="discard" aria-hidden="true" />
          <small>Discard recycled</small>
        </span>
      ) : null}
      {drawCount > 0 ? (
        <span className="alpha-presentation-step is-draw">
          <AlphaGameIcon name="deck" aria-hidden="true" />
          <CardTokens
            count={drawCount}
            labels={drawLabels}
            faceDown={!drawIsPerspective}
            motion="draw"
          />
          <small>{drawIsPerspective ? "Cards drawn" : "Opponent draws"}</small>
        </span>
      ) : null}
    </div>
  );
}
